import { useAppDispatch, useAppSelector } from '@services/store'
import { setAuthToken } from '@services/store/slices/auth'
import { useRefreshTokenMutation } from '@typings/graphql'
import React from 'react'
import { useTranslation } from 'react-i18next'

export type UseXlsxExportProps = {
  path: string
  name: string
  onError: (error: any) => void
  onComplete: () => void
}

export type UseXlsxExport = (data: UseXlsxExportProps) => Promise<void>

export const useXlsxExport = (): UseXlsxExport => {
  const { i18n } = useTranslation()
  const { accessToken, refreshToken } = useAppSelector((state) => state.auth)
  const dispatch = useAppDispatch()

  const [handleTokenRefresh] = useRefreshTokenMutation()

  const tokenRefreshedRef = React.useRef(false)

  const fetchXlsx = React.useCallback(async (token: string, path: string) => {
    return await fetch(`${import.meta.env.REACT_APP_API_ENDPOINT}/${path}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      }
    })
  }, [accessToken])

  const handleUnauthorized = React.useCallback(async () => {
    const response = await handleTokenRefresh({
      variables: {
        token: refreshToken
      }
    })

    if (response.data) {
      dispatch(setAuthToken({
        accessToken: response.data.refreshToken.accessToken,
        refreshToken: response.data.refreshToken.refreshToken
      }))
    }

    tokenRefreshedRef.current = true

    return response.data?.refreshToken.accessToken
  }, [refreshToken])

  const handleExport = React.useCallback(async (data: UseXlsxExportProps, token?: string) => {
    const { path, name, onError, onComplete } = data

    try {
      await fetchXlsx(token || accessToken, path).then(async (response) => {
        if (response.status === 401 && !tokenRefreshedRef.current) {
          const newToken = await handleUnauthorized()

          await handleExport({ path, name, onError, onComplete }, newToken)

          return
        }
        if (!response.ok) {
          throw new Error(response.statusText)
        }

        tokenRefreshedRef.current = false

        response.blob().then((blob) => {
          const url = window.URL.createObjectURL(
            new Blob([blob])
          )

          const dateFormatted = new Intl.DateTimeFormat(i18n.language, {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
          }).format(new Date())

          const date = dateFormatted.replace(/\./g, '-')

          const link = document.createElement('a')
          link.href = url
          link.setAttribute('download', `${name}-${date}.xlsx`)

          document.body.appendChild(link)
          link.click()
          link.parentNode?.removeChild(link)
        })
      }).catch((error) => {
        throw error
      })

      onComplete()
    } catch (e) {
      onError(e)
    }
  }, [accessToken])

  return handleExport
}
